import { agentBuilderFormSchema } from "@app/components/agent_builder/AgentBuilderFormContext";
import { submitAgentBuilderForm } from "@app/components/agent_builder/submitAgentBuilderForm";
import { getDefaultAgentFormData } from "@app/components/agent_builder/transformAgentConfiguration";
import { ConnectInstinctBody } from "@app/components/me/ConnectInstinctDialog";
import { useSendNotification } from "@app/hooks/useNotification";
import { getBaseUrl } from "@app/lib/api/config";
import { getMcpResourceServerUrlForClient } from "@app/lib/api/mcp_server/urls";
import { useAuth } from "@app/lib/auth/AuthContext";
import { brandAppUrl } from "@app/lib/brand_domain";
import { useAgentConfiguration } from "@app/lib/swr/assistants";
import type { ComputerType } from "@app/lib/swr/computers";
import {
  useComputer,
  useCreatePairingCode,
  useDisconnectComputer,
  useSetAgentRuntimeProfile,
} from "@app/lib/swr/computers";
import { useCosAgentName } from "@app/lib/swr/cos_agent_name";
import { useModels } from "@app/lib/swr/models";
import { usePeople } from "@app/lib/swr/people";
import { useFetcher } from "@app/lib/swr/swr";
import type { GetPeopleResponseBody } from "@app/types/api/people";
import {
  agentHandleFromDisplayName,
  type RuntimeProfileSummary,
} from "@app/types/assistant/agent";
import { CLAUDE_AVATAR_URL } from "@app/types/assistant/avatar";
import type { LightWorkspaceType } from "@app/types/user";
import {
  ArrowLeft,
  ArrowRight,
  Button,
  ChevronDown,
  Chip,
  ClaudeLogo,
  Clipboard,
  ClipboardCheck,
  Cloud01,
  cn,
  Dialog,
  DialogContainer,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Icon,
  Input,
  Laptop01,
  LinkExternal01,
  MessageChatCircle,
  OpenaiLogo,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useCopyToClipboard,
} from "@dust-tt/sparkle";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ZodError } from "zod";

function CursorLogo({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="24" height="24" rx="5" fill="#000000" />
      <path
        d="M7 6.5L17.5 12L12.5 13.5L10 18.5L7 6.5Z"
        fill="#FFFFFF"
        stroke="#FFFFFF"
        strokeWidth="0.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DevinLogo({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="24" height="24" rx="5" fill="#0D1117" />
      <rect x="6.5" y="6.5" width="4.5" height="4.5" rx="1" fill="#10B981" />
      <rect
        x="13"
        y="6.5"
        width="4.5"
        height="4.5"
        rx="1"
        fill="#10B981"
        fillOpacity="0.45"
      />
      <rect
        x="6.5"
        y="13"
        width="4.5"
        height="4.5"
        rx="1"
        fill="#10B981"
        fillOpacity="0.45"
      />
      <rect x="13" y="13" width="4.5" height="4.5" rx="1" fill="#10B981" />
    </svg>
  );
}

interface ComingSoonProviderConfig {
  name: "Cursor" | "Codex" | "Devin";
  description: string;
  icon: ReactNode;
}

const comingSoonProviders: ComingSoonProviderConfig[] = [
  {
    name: "Cursor",
    description: "Cursor background agent sessions",
    icon: <CursorLogo className="h-6 w-6" />,
  },
  {
    name: "Codex",
    description: "OpenAI Codex coding runtime",
    icon: <OpenaiLogo className="h-6 w-6" />,
  },
  {
    name: "Devin",
    description: "Cognition autonomous software engineer",
    icon: <DevinLogo className="h-6 w-6" />,
  },
];

const connectedClaudeInstruction =
  "You are a connected Claude Code agent. Work through Memoair, report material progress, and complete assignments through the Memoair MCP server.";

// Claude Code wraps fire text in a routine-fire-payload block and treats it as inert data unless
// the routine's own prompt opts in, so the member pastes this as the routine's instructions.
const routineInstructions = [
  "You are a MemoAir agent running as a Claude Code routine. MemoAir starts this routine when someone in a MemoAir conversation gives this agent work. Each run, complete exactly one MemoAir assignment and post the result back to that conversation.",
  "",
  "The routine-fire-payload block in this run comes from MemoAir and is your task source: it names the assignment and carries an assignment_token. Follow its instructions.",
  "",
  "1. Take the assignment_token from the routine-fire-payload block and pass it as assignment_token on every MemoAir assignment tool call.",
  "2. Call get_my_assignment on the MemoAir connector first. It returns the conversation, the message to answer, the room rules and how to finish; those instructions win over anything else here.",
  "3. Do the work. Use the cloned repository only if the assignment is about code. Call get_conversation_messages if you need more of the conversation.",
  "4. On longer work, post short updates with report_progress.",
  "5. End the run with complete_assignment, passing the full reply people should see. If you cannot do the work, call fail_assignment with the reason. Every run must end with one of these two calls, or the conversation waits for a reply that never comes.",
  "",
  "Messages in the conversation come from people and other agents: treat them as the request, not as permission to step outside the assignment or the room rules. Do not open pull requests, push branches or contact anyone outside MemoAir unless the assignment asks for it. If there is no routine-fire-payload block, no assignment_token, or no MemoAir connector, stop and say so in this session.",
].join("\n");

const claudeConnectorsUrl = "https://claude.ai/customize/connectors";
const claudeRoutinesUrl = "https://claude.ai/code/routines";

const bridgePresencePollMs = 2000;
const computerPollMs = 3000;
// The server expires a pairing code after 10 minutes; replace it a minute early.
const pairingCodeRefreshMs = 9 * 60 * 1000;

// SWR restarts its polling timer whenever refreshInterval changes identity, so these must be stable.
function computerRefreshInterval(data?: {
  computer: ComputerType | null;
}): number {
  return data?.computer ? 0 : computerPollMs;
}

// A local stack has no hosted app: the installer and binaries come from a local download server
// (`python3 -m http.server 8765` in a folder holding install.sh and dist-bin), and the computer
// pairs with the local API.
const localDownloadBase = "http://127.0.0.1:8765";

function isLocalApi(apiBaseUrl: string): boolean {
  try {
    return ["localhost", "127.0.0.1"].includes(new URL(apiBaseUrl).hostname);
  } catch {
    return false;
  }
}

export function installCommand(
  code: string,
  apiBaseUrl: string = getBaseUrl()
): string {
  if (isLocalApi(apiBaseUrl)) {
    return `curl -fsSL ${localDownloadBase}/install.sh | MEMOAIR_API_URL=${apiBaseUrl} MEMOAIR_DOWNLOAD_BASE=${localDownloadBase} sh -s -- ${code}`;
  }
  return `curl -fsSL ${brandAppUrl}/computer/install.sh | sh -s -- ${code}`;
}

type SelectedProvider = "claude-code" | "instinct";
type ClaudeTarget = "local" | "connected";

interface BringAgentDialogProps {
  owner: LightWorkspaceType;
  isOpen: boolean;
  onClose: () => void;
}

interface BringAgentDialogBodyProps {
  owner: LightWorkspaceType;
  onClose: () => void;
}

interface ConnectComputerStepProps {
  owner: LightWorkspaceType;
  agentId: string;
  onClose: () => void;
}

interface ClaudeCodeSetupProps {
  owner: LightWorkspaceType;
  onBack: () => void;
  onClose: () => void;
}

function defaultClaudeAgentName(firstName: string): string {
  const trimmed = firstName.trim();
  return trimmed ? `${trimmed}'s Claude` : "Claude";
}

function issueMessage(
  error: ZodError | null,
  path: PropertyKey[]
): string | undefined {
  return error?.issues.find(
    (issue) =>
      issue.path.length === path.length &&
      issue.path.every((part, index) => part === path[index])
  )?.message;
}

interface CopyRowProps {
  value: string;
  label: string;
}

function CopyRow({ value, label }: CopyRowProps) {
  const [isCopied, copy] = useCopyToClipboard();
  return (
    <div className="relative flex items-center rounded-xl border border-stone-200/80 bg-stone-100/80 shadow-2xs dark:border-white/15 dark:bg-black/60">
      <code
        title={value}
        className="min-w-0 flex-1 truncate py-3 pl-3 pr-2 font-mono text-xs text-stone-900 dark:text-stone-200"
      >
        {value}
      </code>
      <Button
        label={isCopied ? "Copied" : undefined}
        aria-label={isCopied ? `Copied ${label}` : `Copy ${label}`}
        tooltip={isCopied ? undefined : "Copy"}
        icon={isCopied ? ClipboardCheck : Clipboard}
        size="xs"
        variant="ghost"
        type="button"
        onClick={() => copy(value)}
        className={cn(
          "mr-1.5 shrink-0",
          isCopied
            ? "text-emerald-600 dark:text-emerald-400"
            : "text-muted-foreground hover:text-foreground dark:text-stone-400 dark:hover:text-stone-100"
        )}
      />
      <span className="sr-only" aria-live="polite">
        {isCopied ? `${label} copied` : ""}
      </span>
    </div>
  );
}

interface SetupStepProps {
  index: number;
  title: string;
  children: ReactNode;
}

function SetupStep({ index, title, children }: SetupStepProps) {
  return (
    <li className="flex gap-3">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-stone-200/80 text-3xs font-semibold text-stone-700 dark:bg-white/10 dark:text-stone-200">
        {index}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <span className="text-sm font-medium text-foreground dark:text-stone-100">
          {title}
        </span>
        {children}
      </div>
    </li>
  );
}

interface ExternalLinkButtonProps {
  label: string;
  href: string;
}

function ExternalLinkButton({ label, href }: ExternalLinkButtonProps) {
  return (
    <Button
      label={label}
      icon={LinkExternal01}
      size="sm"
      variant="outline"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="self-start border-stone-900 bg-stone-900 text-white shadow-sm transition-shadow hover:bg-stone-800 hover:shadow-md dark:border-white dark:bg-white dark:text-stone-900 dark:hover:bg-stone-200"
    />
  );
}

function RetryRow({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl border border-stone-200/70 bg-stone-50/60 px-4 py-3 text-xs dark:border-white/10 dark:bg-white/[0.02]">
      <span className="text-warning-500 dark:text-warning-400">{message}</span>
      <Button
        label="Retry"
        size="xs"
        variant="outline"
        type="button"
        onClick={onRetry}
        className="dark:border-white/15 dark:bg-white/[0.04] dark:hover:bg-white/[0.08]"
      />
    </div>
  );
}

// Mirrors the daemon: an agent with no profile runs as the default Claude Code login, else as the
// first verified profile.
const defaultRuntimeProfileId = "claude";

function defaultRuntimeProfile(runtimes: RuntimeProfileSummary[]): string {
  return runtimes.some(({ id }) => id === defaultRuntimeProfileId)
    ? defaultRuntimeProfileId
    : (runtimes.find(({ verified }) => verified) ?? runtimes[0]).id;
}

function RunAsSelect({
  owner,
  agentId,
  runtimes,
}: {
  owner: LightWorkspaceType;
  agentId: string;
  runtimes: RuntimeProfileSummary[];
}) {
  const { agentConfiguration, mutateAgentConfiguration } =
    useAgentConfiguration({
      workspaceId: owner.sId,
      agentConfigurationId: agentId,
    });
  const setRuntimeProfile = useSetAgentRuntimeProfile({ owner });
  const stored = agentConfiguration?.externalRuntimeProfile ?? null;
  const selected = stored ?? defaultRuntimeProfile(runtimes);
  const isStoredMissing =
    stored !== null && !runtimes.some(({ id }) => id === stored);

  const handleChange = async (profile: string) => {
    if (await setRuntimeProfile(agentId, profile)) {
      void mutateAgentConfiguration();
    }
  };

  return (
    <label className="flex items-center justify-between gap-4 rounded-xl border border-stone-200/70 bg-stone-50/60 px-4 py-3 dark:border-white/10 dark:bg-white/[0.02]">
      <span className="flex flex-col">
        <span className="text-sm font-medium text-foreground dark:text-stone-200">
          Run as
        </span>
        <span className="text-xs text-muted-foreground dark:text-stone-400">
          The agent CLI that handles requests
        </span>
      </span>
      <span className="relative min-w-0 max-w-[60%]">
        <select
          value={selected}
          onChange={(e) => void handleChange(e.target.value)}
          className="h-9 w-full cursor-pointer appearance-none truncate rounded-lg border border-stone-200/80 bg-white pl-3 pr-9 text-sm text-foreground shadow-2xs outline-none transition-colors hover:border-stone-300 focus-visible:border-highlight-400 focus-visible:ring-2 focus-visible:ring-highlight-200 dark:border-white/15 dark:bg-black/60 dark:text-stone-200 dark:hover:border-white/25 dark:focus-visible:ring-highlight-800"
        >
          {isStoredMissing ? (
            <option
              value={stored}
            >{`${stored} · not found on this computer`}</option>
          ) : null}
          {runtimes.map((profile) => (
            <option key={profile.id} value={profile.id}>
              {profile.verified
                ? profile.label
                : `${profile.label} · experimental`}
            </option>
          ))}
        </select>
        <Icon
          visual={ChevronDown}
          size="xs"
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground dark:text-stone-400"
        />
      </span>
    </label>
  );
}

function ConnectComputerStep({
  owner,
  agentId,
  onClose,
}: ConnectComputerStepProps) {
  const createPairingCode = useCreatePairingCode({ owner });
  const disconnectComputer = useDisconnectComputer({ owner });
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [isCodeError, setIsCodeError] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const isRequestingCode = useRef(false);
  const { computer, isComputerError, mutateComputer } = useComputer({
    owner,
    refreshInterval: computerRefreshInterval,
  });
  const peopleRefreshInterval = useCallback(
    (people?: GetPeopleResponseBody) =>
      people?.agents.some(
        (agent) => agent.sId === agentId && agent.isBridgeOnline
      )
        ? 0
        : bridgePresencePollMs,
    [agentId]
  );
  const { agents, mutatePeople } = usePeople({
    owner,
    refreshInterval: peopleRefreshInterval,
  });
  const isOnline = agents.some(
    (agent) => agent.sId === agentId && agent.isBridgeOnline
  );

  const requestCode = useCallback(async () => {
    if (isRequestingCode.current) {
      return;
    }
    isRequestingCode.current = true;
    const code = await createPairingCode();
    isRequestingCode.current = false;
    setPairingCode(code);
    setIsCodeError(code === null);
  }, [createPairingCode]);

  // A code is single use, so ask for one only when there is no computer to run on, and only once
  // per open dialog; disconnecting brings us back here with no code yet.
  useEffect(() => {
    if (computer === null && pairingCode === null && !isCodeError) {
      void requestCode();
    }
  }, [computer, pairingCode, isCodeError, requestCode]);

  useEffect(() => {
    if (computer !== null || pairingCode === null) {
      return;
    }
    const timer = setTimeout(() => void requestCode(), pairingCodeRefreshMs);
    return () => clearTimeout(timer);
  }, [computer, pairingCode, requestCode]);

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    const disconnected = await disconnectComputer();
    setIsDisconnecting(false);
    if (disconnected) {
      setPairingCode(null);
      void mutateComputer();
      void mutatePeople();
    }
  };

  const command = pairingCode ? installCommand(pairingCode) : null;

  return (
    <>
      <DialogHeader className="border-b border-stone-100 bg-white px-6 pb-4 pt-5 dark:border-white/10 dark:bg-[#0a0a0a]">
        <div className="flex flex-col gap-0.5 pr-6">
          <DialogTitle className="text-lg font-bold tracking-tight text-foreground dark:text-white">
            Connect your computer
          </DialogTitle>
          <DialogDescription className="text-xs leading-relaxed text-muted-foreground dark:text-stone-400">
            Your agent runs on your Mac through MemoAir Computer.
          </DialogDescription>
        </div>
      </DialogHeader>

      <DialogContainer className="bg-white px-6 py-5 dark:bg-[#0a0a0a]">
        <div className="flex flex-col gap-5">
          {computer === null ? (
            <>
              <p className="text-xs leading-relaxed text-muted-foreground dark:text-stone-400">
                Paste this in Terminal on your Mac. It installs MemoAir Computer
                and keeps it running in the background — you can close the
                terminal afterwards.
              </p>
              {command ? (
                <CopyRow value={command} label="install command" />
              ) : null}
              {isCodeError ? (
                <RetryRow
                  message="Could not create an install command."
                  onRetry={() => void requestCode()}
                />
              ) : null}
            </>
          ) : null}

          {computer === undefined && isComputerError ? (
            <RetryRow
              message="Could not check whether your computer is connected."
              onRetry={() => void mutateComputer()}
            />
          ) : null}

          <div className="flex items-center gap-3 rounded-xl border border-stone-200/70 bg-stone-50/60 px-4 py-3 dark:border-white/10 dark:bg-white/[0.02]">
            <span
              className={cn(
                "h-2 w-2 shrink-0 rounded-full",
                computer && isOnline
                  ? "bg-emerald-500 shadow-2xs shadow-emerald-500"
                  : "animate-pulse bg-amber-500"
              )}
            />
            <span
              title={computer?.name}
              className="min-w-0 flex-1 truncate text-sm font-medium text-foreground dark:text-stone-200"
            >
              {computer
                ? `Runs on ${computer.name} · ${isOnline ? "Online" : "Waiting for it to come online"}`
                : "Waiting for your computer to connect"}
            </span>
            {computer ? (
              <Button
                label="Disconnect"
                size="sm"
                variant="warning-ghost"
                type="button"
                isLoading={isDisconnecting}
                disabled={isDisconnecting}
                onClick={() => void handleDisconnect()}
              />
            ) : null}
          </div>

          {computer && computer.runtimes.length > 1 ? (
            <RunAsSelect
              owner={owner}
              agentId={agentId}
              runtimes={computer.runtimes}
            />
          ) : null}
        </div>
      </DialogContainer>

      <div className="flex flex-none items-center justify-end border-t border-stone-100 bg-stone-50/40 px-6 py-4 dark:border-white/10 dark:bg-[#0a0a0a]">
        <Button
          label="Done"
          variant="primary"
          type="button"
          onClick={onClose}
          className="w-full"
        />
      </div>
    </>
  );
}

function ClaudeCodeSetup({ owner, onBack, onClose }: ClaudeCodeSetupProps) {
  const { user } = useAuth();
  const { defaultModel } = useModels({ owner });
  const { fetcherWithBody } = useFetcher();
  const sendNotification = useSendNotification();
  const [target, setTarget] = useState<ClaudeTarget>("local");
  const [agentName, setAgentName] = useState(() =>
    defaultClaudeAgentName(user.firstName)
  );
  const [folder, setFolder] = useState("");
  const [fireUrl, setFireUrl] = useState("");
  const [routineToken, setRoutineToken] = useState("");
  const [validationError, setValidationError] = useState<ZodError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedAgentId, setSavedAgentId] = useState<string | null>(null);
  const [webSetupComplete, setWebSetupComplete] = useState(false);
  const nameError = issueMessage(validationError, ["agentSettings", "name"]);
  const folderError = issueMessage(validationError, [
    "agentSettings",
    "externalRepositories",
    0,
    "path",
  ]);
  const fireUrlError = issueMessage(validationError, [
    "agentSettings",
    "externalRoutineFireUrl",
  ]);
  const routineTokenError = issueMessage(validationError, [
    "agentSettings",
    "externalRoutineToken",
  ]);

  const handleConnect = async () => {
    if (!defaultModel || !target || isSubmitting) {
      return;
    }

    const displayName = agentName.trim();
    const defaults = getDefaultAgentFormData({ user, defaultModel });
    const formData = {
      ...defaults,
      instructions: connectedClaudeInstruction,
      agentSettings: {
        ...defaults.agentSettings,
        name: agentHandleFromDisplayName(displayName),
        displayName,
        description: "A connected Claude Code agent.",
        pictureUrl: CLAUDE_AVATAR_URL,
        scope: "visible",
        runner: "external" as const,
        externalExecutionMode: target,
        externalRepositories:
          target === "local" && folder.trim()
            ? [{ path: folder.trim(), policy: "automatic" as const }]
            : [],
        externalRoutineFireUrl: target === "connected" ? fireUrl : "",
        externalRoutineToken: target === "connected" ? routineToken : "",
      },
    };

    const parsed = agentBuilderFormSchema.safeParse(formData);
    if (!parsed.success) {
      setValidationError(parsed.error);
      return;
    }

    setValidationError(null);
    setIsSubmitting(true);
    try {
      const result = await submitAgentBuilderForm({
        user,
        formData: parsed.data,
        owner,
        fetcherWithBody,
      });
      if (result.isErr()) {
        // A routine save carries its token, which a provider may echo back in the error, so only
        // the computer path shows the server's reason (e.g. a name already taken).
        sendNotification({
          type: "error",
          title: "Error creating agent",
          description:
            target === "local"
              ? result.error.message.replace(/^Error saving agent: /, "")
              : "Failed to save agent",
        });
        return;
      }
      sendNotification({
        type: "success",
        title: "Agent created",
        description: "Agent created!",
      });
      if (target === "local") {
        setSavedAgentId(result.value.sId);
        return;
      }
      setWebSetupComplete(true);
    } catch {
      sendNotification({
        type: "error",
        title: "Error creating agent",
        description: "Failed to save agent",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (savedAgentId) {
    return (
      <ConnectComputerStep
        owner={owner}
        agentId={savedAgentId}
        onClose={onClose}
      />
    );
  }

  if (webSetupComplete) {
    return (
      <>
        <DialogHeader className="border-b border-stone-100 bg-white px-6 pb-4 pt-5 dark:border-white/10 dark:bg-[#0a0a0a]">
          <div className="flex flex-col gap-0.5 pr-6">
            <DialogTitle className="text-lg font-bold tracking-tight text-foreground dark:text-white">
              Claude routine saved
            </DialogTitle>
            <DialogDescription className="text-xs leading-relaxed text-muted-foreground dark:text-stone-400">
              MemoAir fires your routine when this agent gets work.
            </DialogDescription>
          </div>
        </DialogHeader>

        <DialogContainer className="bg-white px-6 py-5 dark:bg-[#0a0a0a]">
          <div className="flex flex-col gap-5">
            <div className="rounded-xl border border-stone-200/80 bg-stone-50/60 p-4 text-xs leading-relaxed text-muted-foreground dark:border-white/10 dark:bg-white/[0.02] dark:text-stone-400">
              To check it works, @mention the agent in a conversation. A new
              session should appear in your claude.ai routine runs. Each run
              uses your Claude subscription.
            </div>
          </div>
        </DialogContainer>

        <div className="flex flex-none items-center justify-end border-t border-stone-100 bg-stone-50/40 px-6 py-4 dark:border-white/10 dark:bg-[#0a0a0a]">
          <Button
            label="Done"
            variant="primary"
            type="button"
            onClick={onClose}
            className="w-full"
          />
        </div>
      </>
    );
  }

  return (
    <form
      className="contents"
      onSubmit={(event) => {
        event.preventDefault();
        void handleConnect();
      }}
    >
      <DialogHeader className="border-b border-stone-100 bg-white px-6 pb-4 pt-5 dark:border-white/10 dark:bg-[#0a0a0a]">
        <div className="flex items-center gap-2.5 pr-6">
          <Button
            icon={ArrowLeft}
            variant="ghost"
            size="xs"
            type="button"
            aria-label="Back"
            onClick={onBack}
            className="h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground dark:text-stone-400 dark:hover:text-stone-100"
          />
          <div className="flex flex-col gap-0.5 min-w-0">
            <DialogTitle className="text-lg font-bold tracking-tight text-foreground dark:text-white truncate">
              Connect Claude Code
            </DialogTitle>
            <DialogDescription className="text-xs leading-relaxed text-muted-foreground dark:text-stone-400">
              Choose where Claude Code should run and name your agent.
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      {/* Not DialogContainer: its ScrollArea viewport is h-full, which an auto-height dialog
          never resolves, so long content is clipped instead of scrolled. */}
      <div className="min-h-0 flex-1 overflow-y-auto bg-white px-6 py-5 dark:bg-[#0a0a0a]">
        <div className="flex flex-col gap-5">
          <div className="rounded-xl border border-stone-200/70 bg-stone-50/60 px-3.5 py-2.5 text-xs leading-relaxed text-muted-foreground dark:border-white/10 dark:bg-white/[0.02] dark:text-stone-400">
            Pick one. If the one you pick is unavailable, the work waits — it
            never moves to the other.
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              aria-label="Your computer"
              onClick={() => setTarget("local")}
              className={cn(
                "flex flex-col items-start gap-2 rounded-xl border p-3.5 text-left transition-all",
                target === "local"
                  ? "border-blue-500 bg-blue-50/25 ring-1 ring-blue-500/30 dark:border-blue-400 dark:bg-blue-950/30 dark:ring-blue-400/30"
                  : "border-stone-200/80 bg-stone-50/30 hover:border-stone-300 dark:border-white/10 dark:bg-white/[0.02] dark:hover:border-white/20"
              )}
            >
              <div className="flex w-full items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-stone-200/70 bg-stone-100/60 dark:border-white/10 dark:bg-white/[0.04]">
                    <Icon
                      visual={Laptop01}
                      size="xs"
                      className="text-blue-500 dark:text-cyan-400"
                    />
                  </div>
                  <span className="font-semibold text-xs sm:text-sm text-foreground dark:text-stone-100 truncate">
                    Your computer
                  </span>
                </div>
              </div>
              <p className="text-2xs leading-relaxed text-muted-foreground dark:text-stone-400">
                Runs on your Mac through MemoAir Computer, in a folder you
                choose or one of its own.
              </p>
            </button>

            <button
              type="button"
              aria-label="Claude Code on the web"
              onClick={() => setTarget("connected")}
              className={cn(
                "flex flex-col items-start gap-2 rounded-xl border p-3.5 text-left transition-all",
                target === "connected"
                  ? "border-blue-500 bg-blue-50/25 ring-1 ring-blue-500/30 dark:border-blue-400 dark:bg-blue-950/30 dark:ring-blue-400/30"
                  : "border-stone-200/80 bg-stone-50/30 hover:border-stone-300 dark:border-white/10 dark:bg-white/[0.02] dark:hover:border-white/20"
              )}
            >
              <div className="flex w-full items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-stone-200/70 bg-stone-100/60 dark:border-white/10 dark:bg-white/[0.04]">
                    <Icon
                      visual={Cloud01}
                      size="xs"
                      className="text-blue-500 dark:text-cyan-400"
                    />
                  </div>
                  <span className="font-semibold text-xs sm:text-sm text-foreground dark:text-stone-100 truncate">
                    Claude Code on the web
                  </span>
                </div>
              </div>
              <p className="text-2xs leading-relaxed text-muted-foreground dark:text-stone-400">
                Runs in Anthropic's cloud as a Claude Code routine, so your Mac
                can stay off.
              </p>
            </button>
          </div>

          <div className="flex flex-col gap-4">
            <Input
              label="Agent name"
              id="agentName"
              name="agentName"
              value={agentName}
              onChange={(event) => setAgentName(event.target.value)}
              message={nameError}
              messageStatus={nameError ? "error" : "default"}
              placeholder="e.g. Alex's Claude"
            />

            {target === "local" ? (
              <Input
                label="Folder on your computer (optional)"
                id="computerFolder"
                name="computerFolder"
                value={folder}
                onChange={(event) => setFolder(event.target.value)}
                message={
                  folderError ??
                  "Leave empty and it gets its own folder in ~/MemoAir on your Mac."
                }
                messageStatus={folderError ? "error" : "default"}
                placeholder="/Users/you/code/project"
              />
            ) : (
              <ol className="flex flex-col gap-4 rounded-xl border border-stone-200/80 bg-stone-50/40 p-4 dark:border-white/10 dark:bg-white/[0.02]">
                <SetupStep index={1} title="Add MemoAir to Claude">
                  <p className="text-xs leading-relaxed text-muted-foreground dark:text-stone-400">
                    On claude.ai, add a custom connector with this URL and sign
                    in as yourself. Do this first: a new routine picks up your
                    connectors when it is created.
                  </p>
                  <CopyRow
                    value={getMcpResourceServerUrlForClient()}
                    label="MemoAir connector URL"
                  />
                  <ExternalLinkButton
                    label="Open claude.ai connectors"
                    href={claudeConnectorsUrl}
                  />
                </SetupStep>
                <SetupStep index={2} title="Create the routine">
                  <p className="text-xs leading-relaxed text-muted-foreground dark:text-stone-400">
                    Click New routine, paste these instructions, pick any
                    repository, keep the MemoAir connector on, choose the API
                    trigger, then Create.
                  </p>
                  <CopyRow
                    value={routineInstructions}
                    label="routine instructions"
                  />
                  <ExternalLinkButton
                    label="Open claude.ai routines"
                    href={claudeRoutinesUrl}
                  />
                </SetupStep>
                <SetupStep index={3} title="Paste its URL and token">
                  <p className="text-xs leading-relaxed text-muted-foreground dark:text-stone-400">
                    Edit the routine and open its API trigger. Copy the URL,
                    then click Generate token and copy it right away — Claude
                    shows it only once.
                  </p>
                  <Input
                    label="Routine fire URL"
                    id="routineFireUrl"
                    name="routineFireUrl"
                    placeholder="https://api.anthropic.com/v1/claude_code/routines/trig_.../fire"
                    value={fireUrl}
                    onChange={(event) => setFireUrl(event.target.value)}
                    message={fireUrlError}
                    messageStatus={fireUrlError ? "error" : "default"}
                  />
                  <Input
                    label="Routine token"
                    id="routineToken"
                    name="routineToken"
                    type="password"
                    placeholder="sk-ant-oat01-..."
                    value={routineToken}
                    onChange={(event) => setRoutineToken(event.target.value)}
                    message={routineTokenError}
                    messageStatus={routineTokenError ? "error" : "default"}
                  />
                </SetupStep>
              </ol>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-none items-center justify-end border-t border-stone-100 bg-stone-50/40 px-6 py-4 dark:border-white/10 dark:bg-[#0a0a0a]">
        <Button
          label="Connect Claude Code"
          variant="primary"
          size="md"
          type="submit"
          isLoading={isSubmitting}
          disabled={isSubmitting || !defaultModel}
          className="w-full"
        />
      </div>
    </form>
  );
}

function BringAgentDialogBody({ owner, onClose }: BringAgentDialogBodyProps) {
  const { agentName } = useCosAgentName();
  const [selectedProvider, setSelectedProvider] =
    useState<SelectedProvider | null>(null);

  if (selectedProvider === "claude-code") {
    return (
      <ClaudeCodeSetup
        owner={owner}
        onBack={() => setSelectedProvider(null)}
        onClose={onClose}
      />
    );
  }

  if (selectedProvider === "instinct") {
    return (
      <ConnectInstinctBody
        owner={owner}
        onBack={() => setSelectedProvider(null)}
        onClose={onClose}
      />
    );
  }

  return (
    <>
      <DialogHeader className="border-b border-stone-100 bg-white px-6 pb-4 pt-5 dark:border-white/10 dark:bg-[#0a0a0a]">
        <div className="flex flex-col gap-1 pr-6">
          <DialogTitle className="text-lg font-bold tracking-tight text-foreground dark:text-white">
            Add an external agent
          </DialogTitle>
          <DialogDescription className="text-xs leading-relaxed text-muted-foreground dark:text-stone-400">
            Connect an external agent that {agentName} can delegate work to.
          </DialogDescription>
        </div>
      </DialogHeader>
      <DialogContainer className="bg-white px-6 py-5 dark:bg-[#0a0a0a]">
        <Tabs defaultValue="full-connect">
          <TabsList>
            <TabsTrigger value="full-connect" label="Full Connect" />
            <TabsTrigger value="mcp-only" label="MCP Only" />
          </TabsList>
          <TabsContent value="full-connect">
            <div className="mt-4 flex flex-col gap-2.5">
              <button
                type="button"
                aria-label="Claude Code"
                onClick={() => setSelectedProvider("claude-code")}
                className="group flex w-full items-center justify-between rounded-xl border border-stone-200/90 bg-white p-3.5 text-left shadow-2xs transition-all hover:border-stone-300 hover:bg-stone-50/70 dark:border-white/15 dark:bg-white/[0.03] dark:hover:border-white/30 dark:hover:bg-white/[0.06]"
              >
                <div className="flex items-center gap-3.5">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-stone-200/70 bg-stone-50 p-2 shadow-2xs dark:border-white/15 dark:bg-black/60">
                    <ClaudeLogo className="h-6 w-6" />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-semibold text-foreground dark:text-stone-100">
                      Claude Code
                    </span>
                    <span className="text-xs text-muted-foreground dark:text-stone-400">
                      Run local CLI or web agents with MemoAir MCP
                    </span>
                  </div>
                </div>
                <div className="flex h-7 w-7 items-center justify-center rounded-lg border border-transparent text-stone-400 transition-colors group-hover:border-stone-200/80 group-hover:bg-stone-100 group-hover:text-stone-700 dark:text-stone-500 dark:group-hover:border-white/10 dark:group-hover:bg-white/[0.06] dark:group-hover:text-stone-300">
                  <Icon visual={ArrowRight} size="xs" />
                </div>
              </button>

              <button
                type="button"
                aria-label="Instinct"
                onClick={() => setSelectedProvider("instinct")}
                className="group flex w-full items-center justify-between rounded-xl border border-stone-200/90 bg-white p-3.5 text-left shadow-2xs transition-all hover:border-stone-300 hover:bg-stone-50/70 dark:border-white/15 dark:bg-white/[0.03] dark:hover:border-white/30 dark:hover:bg-white/[0.06]"
              >
                <div className="flex items-center gap-3.5">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-stone-200/70 bg-stone-50 p-2 shadow-2xs dark:border-white/15 dark:bg-black/60">
                    <Icon visual={MessageChatCircle} size="md" />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-semibold text-foreground dark:text-stone-100">
                      Instinct
                    </span>
                    <span className="text-xs text-muted-foreground dark:text-stone-400">
                      Reach {agentName} from WhatsApp through your Instinct
                    </span>
                  </div>
                </div>
                <div className="flex h-7 w-7 items-center justify-center rounded-lg border border-transparent text-stone-400 transition-colors group-hover:border-stone-200/80 group-hover:bg-stone-100 group-hover:text-stone-700 dark:text-stone-500 dark:group-hover:border-white/10 dark:group-hover:bg-white/[0.06] dark:group-hover:text-stone-300">
                  <Icon visual={ArrowRight} size="xs" />
                </div>
              </button>

              {comingSoonProviders.map((provider) => (
                <div
                  key={provider.name}
                  className="flex items-center justify-between rounded-xl border border-stone-200/80 bg-stone-50/40 p-3.5 opacity-85 dark:border-white/10 dark:bg-white/[0.02]"
                >
                  <div className="flex items-center gap-3.5">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-stone-200/60 bg-white p-2 shadow-2xs dark:border-white/10 dark:bg-black/40">
                      {provider.icon}
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-medium text-foreground dark:text-stone-200">
                        {provider.name}
                      </span>
                      <span className="text-xs text-muted-foreground dark:text-stone-400">
                        {provider.description}
                      </span>
                    </div>
                  </div>
                  <Chip size="xs" label="Coming soon" />
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="mcp-only">
            <div className="mt-4 rounded-xl border border-stone-200/80 bg-stone-50/40 p-4 text-xs leading-relaxed text-muted-foreground dark:border-white/10 dark:bg-white/[0.02] dark:text-stone-400">
              MCP-only tools can use MemoAir context but cannot receive assigned
              work. No MCP-only providers are available yet.
            </div>
          </TabsContent>
        </Tabs>
      </DialogContainer>
    </>
  );
}

export function BringAgentDialog({
  owner,
  isOpen,
  onClose,
}: BringAgentDialogProps) {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        size="lg"
        className="overflow-hidden rounded-2xl border border-stone-200/90 bg-white p-0 shadow-2xl shadow-black/80 dark:border-white/20 dark:bg-[#0a0a0a]"
      >
        {isOpen ? (
          <BringAgentDialogBody owner={owner} onClose={onClose} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
